/**
 * Mapa de ruta de una reserva: qué se puede dibujar y con qué URL.
 *
 * Los puntos tienen dos ORÍGENES posibles y se prefiere siempre el del
 * proveedor: cuando la oferta elegida trae los extremos que él resolvió
 * (`originPoint`/`destinationPoint`, hoy rail, ferry y transfers), el mapa es
 * el recorrido CONFIRMADO y la card lo dice; cuando no los trae, se cae a los
 * puntos del PEDIDO (los que el asesor eligió al buscar) y la card avisa que
 * son esos. Nunca se mezclan: un extremo confirmado y otro pedido leería como
 * un recorrido que nadie confirmó así.
 *
 * El proveedor de mapas (su URL, sus modos, su vocabulario) queda encapsulado
 * ACÁ: hacia afuera solo hay puntos y un modo de dominio. La API embebida se
 * usa en un iframe porque es la única gratuita sin límite; la JS API y la
 * Static API se facturan por carga.
 *
 * Contrato del proveedor (Google Maps Embed API), en
 * https://developers.google.com/maps/documentation/embed/embedding-map — de
 * ahí salen las tres formas que se arman acá, y ninguna otra:
 *
 * - `/directions`: `origin` y `destination` REQUERIDOS, declarados como
 *   "URL-escaped place name, address, plus code, latitude/longitude
 *   coordinates, or place ID" — la DIRECCIÓN textual es tan válida como el par
 *   lat/lng; `mode` opcional entre `driving | walking | bicycling | transit |
 *   flying`. Marca los dos extremos y traza el recorrido. Y ROTULA cada pin
 *   con lo que se le manda: con coordenadas reverse-geocodea y pone el POI más
 *   cercano, que es cómo el pin de un transfer que salía de Schiphol terminó
 *   diciendo "Bathroom, 1118 AP Schiphol" (#2370). Por eso se le manda la
 *   dirección cuando el punto la tiene — ver `directionsPoint`.
 * - `/place`: `q` REQUERIDO. El contrato lo declara como "place name, address,
 *   plus code, or place ID" — no menciona coordenadas, pero el proveedor
 *   resuelve un par lat/lng y deja el pin en él (verificado en vivo, #2228).
 * - `/view`: `center` REQUERIDO y documentado como par lat/lng; `zoom`
 *   opcional. NO marca nada: solo encuadra.
 *
 * Por eso ferry no traza recorrido: entre dos puertos el único modo que marca
 * ambas coordenadas y las une es `flying`, que las dibuja como un VUELO (ícono
 * de avión y duración de vuelo sobre una travesía que tarda un día). Se
 * encuadra con `/view` y los dos puertos se nombran en la card.
 */

/**
 * De dónde salió el punto: lo que el asesor PIDIÓ al buscar, o lo que el
 * proveedor RESOLVIÓ para la oferta elegida.
 *
 * Espeja el vocabulario que se persiste (`TravelServiceRoutePoint.source`),
 * igual que `RouteMapPoint` espeja su forma. Lo que no está acá se descarta al
 * sanear, así que un valor inesperado del backend no puede llegar a la card.
 */
export type RouteMapPointSource = 'requested' | 'resolved';

/** Punto geolocalizado de un extremo del recorrido. */
export interface RouteMapPoint {
  /** Nombre para mostrar (aeropuerto, hotel, puerto). */
  label?: string;
  /**
   * Dirección textual completa del punto, cuando se la conoce: la que el
   * proveedor resolvió para la oferta o la que el asesor eligió al pedir.
   *
   * Es lo que NOMBRA el extremo en la URL del mapa (`directionsPoint`), y no
   * el `label`: el label es el nombre para mostrar y un nombre suelto no
   * identifica un lugar en el planeta — "Hotel Arena" geocodea a un resort de
   * Punta del Este, verificado en vivo. Una dirección con calle, código postal
   * y país no tiene ese problema.
   *
   * Se PERSISTE con el punto (`RuteroRoutePointDto.address`) porque es parte
   * de cómo se dibujó el recorrido: si no viajara, la reserva ya guardada
   * volvería a nombrar sus extremos por coordenadas y el mismo mapa se
   * rotularía distinto antes y después de reservar.
   */
  address?: string;
  lat: number;
  lng: number;
  /**
   * Ausente = del PEDIDO. Es lo correcto para las reservas guardadas antes de
   * este campo: entonces no había otra fuente que el pedido, así que la falta
   * del rótulo no es un dato desconocido sino el valor viejo.
   */
  source?: RouteMapPointSource;
}

/**
 * Cómo se dibuja el trayecto. Es vocabulario de DOMINIO, no del proveedor de
 * mapas.
 *
 * `transit` es el transporte público de línea —tren y micro de larga
 * distancia—: se viaja por la red y los horarios del operador, no por la ruta
 * que elegiría un auto, que es lo que dibuja `road`. No es un modo POR
 * VERTICAL: rail y buses comparten la misma forma de moverse, y el nombre lo
 * dice para que `routeMapMode('buses', …)` no devuelva algo llamado `rail`.
 *
 * `centered` es "mostrar los dos extremos sin afirmar cómo se va de uno al
 * otro", que para un ferry es lo único honesto: entre dos puertos no hay ruta
 * terrestre, y el único modo del proveedor que marca dos coordenadas y las une
 * las dibuja como un VUELO — ícono de avión y duración de vuelo sobre una
 * travesía que tarda un día. La línea `A → B` de la card nombra los dos
 * puertos igual.
 */
export type RouteMapMode = 'road' | 'transit' | 'centered' | 'single';

/**
 * Topes del punto que viaja al backend. Son LOS MISMOS que valida
 * `RuteroRoutePointDto`/`HoldRuteroBookingDto.routePoints`
 * (`apps/shared/dtos/clients/hold-rutero-booking.dto.ts`): el mapa es display
 * y no puede hacer fallar una reserva, así que acá se recorta en vez de
 * mandar algo que el borde rechace.
 */
const MAX_LABEL = 120;
const MAX_POINTS = 2;
/**
 * Tope de la dirección. Es EL MISMO que valida `RuteroRoutePointDto.address`
 * (`apps/shared/dtos/clients/hold-rutero-booking.dto.ts`), igual que el del
 * rótulo: el mapa es display y no puede hacer fallar una reserva. Una
 * dirección más larga NO se recorta —media dirección geocodea a otro lado— y
 * el extremo se nombra por sus coordenadas, que siempre son correctas.
 */
const MAX_ADDRESS = 200;

const EMBED_BASE = 'https://www.google.com/maps/embed/v1';

/** Modo del proveedor para los que SÍ trazan un recorrido. */
const PROVIDER_TRAVEL_MODE: Record<'road' | 'transit', string> = {
  road: 'driving',
  transit: 'transit',
};

/** Tamaño del tile, para elegir el zoom que entra los dos extremos. */
const TILE_WIDTH = 614;
const TILE_HEIGHT = 210;
/** Lado del tile del proveedor, en píxeles, a zoom 0. */
const PROVIDER_TILE_SIZE = 256;
/** Aire alrededor de los extremos: pegados al borde no se leen. */
const FIT_MARGIN = 0.8;
const MIN_ZOOM = 1;
const MAX_ZOOM = 15;

/**
 * Un punto usable. Vale tanto para lo que arma el formulario como para lo que
 * devuelve el backend: una fila vieja o un valor inesperado no puede terminar
 * en una URL con coordenadas rotas.
 */
export function isRouteMapPoint(value: unknown): value is RouteMapPoint {
  if (!value || typeof value !== 'object') return false;
  const point = value as Record<string, unknown>;
  const { lat, lng, label } = point;
  if (typeof lat !== 'number' || !Number.isFinite(lat)) return false;
  if (typeof lng !== 'number' || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (label !== undefined && typeof label !== 'string') return false;
  return point.address === undefined || typeof point.address === 'string';
}

/**
 * La dirección utilizable de un valor cualquiera, o nada.
 *
 * Vacía no es una dirección, y una más larga que el tope tampoco se usa: se
 * cae a las coordenadas, que es lo que se venía dibujando.
 */
function usableAddress(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const address = value.trim();
  return address && address.length <= MAX_ADDRESS ? address : undefined;
}

/** Solo un rótulo del vocabulario sobrevive; cualquier otra cosa se descarta. */
function pointSource(value: unknown): RouteMapPointSource | undefined {
  return value === 'requested' || value === 'resolved' ? value : undefined;
}

/**
 * Los puntos utilizables de una lista cualquiera, en orden, a lo sumo dos.
 *
 * `source` rotula de dónde salen cuando el llamador lo sabe (arma el pedido, o
 * lee la oferta). Sin él se PRESERVA el que cada punto ya traía, que es el
 * caso de lo que vuelve del backend: una fila guardada antes del rótulo no
 * tiene ninguno y queda, correctamente, como punto del pedido.
 */
export function routeMapPoints(
  value: unknown,
  source?: RouteMapPointSource,
): RouteMapPoint[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(isRouteMapPoint)
    .slice(0, MAX_POINTS)
    .map((point) => {
      const origin = source ?? pointSource(point.source);
      const address = usableAddress(point.address);
      return {
        lat: point.lat,
        lng: point.lng,
        ...(point.label?.trim()
          ? { label: point.label.trim().slice(0, MAX_LABEL) }
          : {}),
        ...(address ? { address } : {}),
        ...(origin ? { source: origin } : {}),
      };
    });
}

/**
 * El recorrido dibujado es el que el PROVEEDOR resolvió, no el que se pidió.
 * Se lee de los MISMOS puntos que se dibujan —no de un flag paralelo que
 * pueda divergir— y exige que lo sean TODOS: con uno solo confirmado, lo que
 * se ve sigue teniendo un extremo sin confirmar.
 */
export function routeMapPointsAreResolved(points: RouteMapPoint[]): boolean {
  return (
    points.length > 0 && points.every((point) => point.source === 'resolved')
  );
}

/**
 * Un extremo del recorrido tal como lo conoce el formulario: su nombre para
 * mostrar y sus coordenadas, si se las pudo resolver.
 */
export interface RouteMapEndpoint {
  label?: string;
  /** Dirección textual del extremo, si el pedido o la oferta la trae. */
  address?: string;
  coords?: { lat: number; lng: number };
}

/**
 * Los dos extremos como puntos de mapa, o VACÍO si a alguno le faltan
 * coordenadas. ÚNICA definición de esa regla, tanto para el pedido de los
 * verticales de dos puntos (transfers, rail) como para lo que resolvió el
 * proveedor: sin los dos no hay recorrido que dibujar, y el que falta jamás
 * se completa con otra cosa (21.23.4 — todo atado al snapshot comiteado).
 */
export function routeMapEndpoints(
  origin: RouteMapEndpoint,
  destination: RouteMapEndpoint,
  source?: RouteMapPointSource,
): RouteMapPoint[] {
  if (!origin.coords || !destination.coords) return [];
  const points = routeMapPoints(
    [
      {
        ...origin.coords,
        ...(origin.label ? { label: origin.label } : {}),
        ...(origin.address ? { address: origin.address } : {}),
      },
      {
        ...destination.coords,
        ...(destination.label ? { label: destination.label } : {}),
        ...(destination.address ? { address: destination.address } : {}),
      },
    ],
    source,
  );
  // `routeMapPoints` descarta lo inutilizable UNO POR UNO: un par roto en un
  // extremo dejaría el otro solo, y un punto solo no es "medio recorrido" —
  // es otro modo de mapa (el pin de `single`). O están los dos, o no hay.
  return points.length === 2 ? points : [];
}

/**
 * Un extremo tal como lo publica el hub en la oferta (`originPoint` /
 * `destinationPoint`): nombre ya resuelto por el proveedor, su dirección
 * completa y las coordenadas. El `name` es lo que se MUESTRA (la línea
 * `A → B` de la card); la `address` es lo que NOMBRA el pin en el mapa. El
 * rango de las coordenadas y el tope de la dirección los valida
 * `routeMapPoints` aguas abajo, con los mismos topes que todo lo demás: una
 * respuesta de tercero no es más confiable que un formulario.
 */
function offerEndpoint(value: unknown): RouteMapEndpoint {
  if (!value || typeof value !== 'object') return {};
  const { name, address, lat, lng } = value as Record<string, unknown>;
  return {
    ...(typeof name === 'string' && name.trim() ? { label: name } : {}),
    ...(typeof address === 'string' && address.trim() ? { address } : {}),
    ...(typeof lat === 'number' && typeof lng === 'number'
      ? { coords: { lat, lng } }
      : {}),
  };
}

/**
 * Los dos extremos que el PROVEEDOR resolvió para esta oferta, o vacío.
 *
 * El hub los publica en las ofertas de rail, ferry y transfers (rutero#66);
 * el resto de los verticales no los trae y cae, por ausencia, al pedido. Vale
 * la misma regla de todo o nada que para el pedido (`routeMapEndpoints`): con
 * un solo extremo resuelto no se completa el otro con el pedido, porque el
 * recorrido mezclado no es el que confirmó el proveedor ni el que se pidió.
 */
export function offerRouteMapPoints(offer: unknown): RouteMapPoint[] {
  if (!offer || typeof offer !== 'object') return [];
  const { originPoint, destinationPoint } = offer as Record<string, unknown>;
  return routeMapEndpoints(
    offerEndpoint(originPoint),
    offerEndpoint(destinationPoint),
    'resolved',
  );
}

function samePoint(a: RouteMapPoint, b: RouteMapPoint): boolean {
  return a.lat === b.lat && a.lng === b.lng;
}

/**
 * Cómo dibujar el trayecto de este vertical, o `null` si no se dibuja.
 *
 * Ningún vertical queda excluido POR SER ÉL: lo que decide es si hay dos
 * puntos distintos con coordenadas. `buses` es el caso que lo motivó — su
 * catálogo hoy no publica coordenadas, así que en la práctica nunca hay
 * puntos y no hay card; el día que el hub las publique el mapa aparece solo,
 * sin tocar esta función (#2255). Excluirlo acá haría que la exclusión
 * sobreviviera al dato y hubiera que acordarse de levantarla.
 *
 * `esims`, `tours` y `passes` no tienen trayecto que dibujar y `circuits` ya
 * trae el mapa del proveedor: esos no dependen de los datos y no tienen case.
 */
export function routeMapMode(
  vertical: string,
  points: RouteMapPoint[],
): RouteMapMode | null {
  if (points.length === 0) return null;
  // Un solo lugar (el auto se retira y se devuelve en la misma oficina): un
  // pin, nunca un trayecto degenerado de un punto a sí mismo.
  const single = points.length === 1 || samePoint(points[0], points[1]);

  switch (vertical) {
  case 'transfers':
    return single ? null : 'road';
  case 'cars':
    return single ? 'single' : 'road';
  // Transporte público de línea: los dos viajan por la red del operador.
  case 'rail':
  case 'buses':
    return single ? null : 'transit';
  case 'ferry':
    return single ? null : 'centered';
  default:
    return null;
  }
}

/**
 * Los extremos resueltos que ESTE vertical puede usar como recorrido. Esta es
 * la decisión compartida por card, mapa y hold: tener dos objetos publicados
 * no alcanza si para el vertical forman un recorrido degenerado.
 */
export function resolvedOfferRouteMapPoints(
  vertical: string,
  offer: unknown,
): RouteMapPoint[] {
  const resolved = offerRouteMapPoints(offer);
  return routeMapMode(vertical, resolved) ? resolved : [];
}

/**
 * Los puntos de una reserva que se está por crear: los que resolvió el
 * proveedor si la oferta los trae, y si no los del pedido.
 *
 * ÚNICA definición de esa preferencia. Es la misma expresión que pinta el
 * mapa y la que se persiste al holdear, para que los puntos guardados sean
 * exactamente los que el asesor vio antes de reservar (21.9): mismos
 * extremos, mismas coordenadas, mismo `source`.
 *
 * Guardado incluye la DIRECCIÓN con la que se nombró cada extremo (#2370),
 * no solo la coordenada: la fila tiene que poder redibujar el mismo mapa que
 * el asesor aprobó, y con la dirección afuera el mismo recorrido se rotularía
 * distinto antes y después de reservar.
 *
 * Lo que la fila fija son los PUNTOS, no el píxel: dónde el proveedor de
 * mapas pone el pin a partir de esa dirección lo decide su geocoder. La
 * autoridad de qué se reservó es la fila —el punto que el proveedor resolvió,
 * con su coordenada y su dirección— y la card la dice en texto arriba del
 * mapa; el iframe es la ilustración, no el registro. Que el pin quede donde
 * geocodea la dirección es el precio explícito de que diga el lugar y no el
 * POI más cercano: ver `directionsPoint`, que documenta las seis formas que
 * acepta el contrato del proveedor y por qué ninguna hace las dos cosas.
 *
 * Preferir el punto resuelto no puede dejar SIN MAPA lo que hoy lo tiene: si
 * el proveedor resolvió los dos extremos al mismo lugar, ese vertical no
 * dibuja nada con ellos y se cae al pedido, que es lo que se venía viendo (la
 * nota sigue diciendo que son los pedidos y que vale la dirección de la
 * oferta). Cambiar un mapa impreciso por ninguno sería una regresión, no una
 * corrección.
 */
export function bookingRouteMapPoints(
  vertical: string,
  offer: unknown,
  requested: RouteMapPoint[],
): RouteMapPoint[] {
  const resolved = resolvedOfferRouteMapPoints(vertical, offer);
  return resolved.length > 0 ? resolved : requested;
}

/**
 * Los puntos tal como se PERSISTEN al holdear: los campos que valida
 * `RuteroRoutePointDto` y ni uno más.
 *
 * El cuerpo del hold se valida con `forbidNonWhitelisted`, así que un campo
 * que el contrato no declare haría fallar la reserva entera. Por eso lo que
 * se dibuja y lo que se guarda salen de la MISMA lista de puntos
 * (`bookingRouteMapPoints`, 21.9) y este es el único lugar donde se le da la
 * forma del contrato — no una segunda derivación que pueda divergir.
 *
 * Va TODO lo que participa del dibujo, la dirección incluida: la fila tiene
 * que poder redibujar el mismo mapa que el asesor aprobó, no uno parecido.
 */
export function holdRoutePoints(points: RouteMapPoint[]): RouteMapPoint[] {
  return points.map((point) => ({
    ...(point.label ? { label: point.label } : {}),
    ...(point.address ? { address: point.address } : {}),
    lat: point.lat,
    lng: point.lng,
    ...(point.source ? { source: point.source } : {}),
  }));
}

/** Sin exponente ni ruido de coma flotante: la URL lleva grados decimales. */
function coord(point: RouteMapPoint): string {
  return `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;
}

/**
 * Con qué se nombra un extremo del recorrido en la URL del proveedor: su
 * dirección textual si la tiene, y si no las coordenadas.
 *
 * El proveedor ROTULA el pin con lo que recibe, y NO existe forma de darle una
 * coordenada y un rótulo a la vez. Las cinco formas que su contrato acepta,
 * probadas en vivo sobre el mismo transfer (#2370):
 *
 * - `lat,lng`: posición exacta, rótulo reverse-geocodeado — el POI más cercano
 *   ("Bathroom, 1118 AP Schiphol" sobre el punto de recogida de Schiphol).
 * - plus code: es la misma coordenada, así que el rótulo vuelve a ser un POI
 *   cualquiera ("Pastorie Sint Elisabethgesticht" sobre el hotel destino).
 * - `lat,lng (Rótulo)`, la sintaxis del Maps clásico: la API embebida NO la
 *   honra — geocodea el texto y DESCARTA la coordenada (el destino se fue a
 *   Punta del Este).
 * - nombre del lugar: geocodeado; un nombre suelto no identifica un lugar en
 *   el planeta ("Hotel Arena" → un resort de Punta del Este). Por eso el
 *   rótulo para mostrar NUNCA se manda.
 * - dirección postal: geocodeada a esa dirección. Es la única que nombra el
 *   pin con el punto de verdad, y por eso es la que se manda.
 *
 * (La sexta forma, `place_id:`, sí rotularía una posición exacta, pero son ids
 * de Google: no los publica el hub y conseguirlos pide una llamada más a una
 * API paga.)
 *
 * El precio es explícito: el pin queda donde el geocoder resuelve la
 * dirección, no en el par lat/lng. Lo que NO se mueve es el recorrido que el
 * sistema conoce: las coordenadas del punto son las mismas que se dibujan en
 * los otros modos y las mismas que se persisten. Sin dirección, coordenadas:
 * exactamente lo que se dibuja hoy.
 */
function directionsPoint(point: RouteMapPoint, useAddress = true): string {
  // Se sanea acá también, y no solo aguas arriba: esta función arma la URL
  // que se le pide al proveedor, y una dirección vacía o desmedida no puede
  // llegar a ella por ningún camino.
  return (useAddress ? usableAddress(point.address) : undefined) ?? coord(point);
}

/**
 * ¿Las direcciones de los dos extremos SIRVEN para distinguirlos?
 *
 * Si los dos traen el mismo texto —un traslado que sale y llega al mismo
 * hotel, un proveedor que resuelve los dos puntos a la misma dirección
 * postal— nombrarlos así los colapsaría en un solo lugar, y el recorrido que
 * el asesor vio (dos puntos distintos, con coordenadas distintas) dejaría de
 * dibujarse. Ahí manda la coordenada, que es lo único que los separa: el mapa
 * pierde el rótulo bueno, no el recorrido.
 */
function addressesTellEndpointsApart(
  origin: RouteMapPoint,
  destination: RouteMapPoint,
): boolean {
  const from = usableAddress(origin.address);
  const to = usableAddress(destination.address);
  return !from || !to || from !== to;
}

/** Solo un código de idioma reconocible viaja a la URL del proveedor. */
function embedLanguage(language: string | undefined): string | undefined {
  const code = language?.slice(0, 2).toLowerCase();
  return code && /^[a-z]{2}$/.test(code) ? code : undefined;
}

/**
 * La latitud como fracción del mundo proyectado (0 = norte, 1 = sur). El mapa
 * es Mercator: un grado de latitud NO ocupa lo mismo en el ecuador que cerca
 * del polo, así que promediar grados dejaría un extremo fuera del tile.
 */
function mercatorFraction(lat: number): number {
  const sin = Math.sin((lat * Math.PI) / 180);
  const clamped = Math.min(Math.max(sin, -0.9999), 0.9999);
  return 0.5 - Math.log((1 + clamped) / (1 - clamped)) / (4 * Math.PI);
}

function latFromMercatorFraction(fraction: number): number {
  const projected = Math.PI * (1 - 2 * fraction);
  return (180 / Math.PI) * Math.atan(Math.sinh(projected));
}

/** La diferencia de longitud por el camino corto (nunca dando la vuelta). */
function shortestLngDelta(from: number, to: number): number {
  const delta = ((to - from + 540) % 360) - 180;
  return delta;
}

/** El punto medio real entre los dos extremos, en la proyección del mapa. */
function centerOf(a: RouteMapPoint, b: RouteMapPoint): RouteMapPoint {
  const lng = a.lng + shortestLngDelta(a.lng, b.lng) / 2;
  return {
    lat: latFromMercatorFraction(
      (mercatorFraction(a.lat) + mercatorFraction(b.lat)) / 2,
    ),
    lng: ((lng + 540) % 360) - 180,
  };
}

/** El zoom más cercano en que los DOS extremos entran en el tile. */
function fitZoom(a: RouteMapPoint, b: RouteMapPoint): number {
  const lngFraction = Math.abs(shortestLngDelta(a.lng, b.lng)) / 360;
  const latFraction = Math.abs(
    mercatorFraction(a.lat) - mercatorFraction(b.lat),
  );

  const zoomFor = (fraction: number, viewport: number) =>
    fraction > 0
      ? Math.log2((viewport * FIT_MARGIN) / (PROVIDER_TILE_SIZE * fraction))
      : MAX_ZOOM;

  const zoom = Math.floor(
    Math.min(
      zoomFor(lngFraction, TILE_WIDTH),
      zoomFor(latFraction, TILE_HEIGHT),
    ),
  );
  return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
}

export interface RouteMapEmbedInput {
  points: RouteMapPoint[];
  mode: RouteMapMode;
  /** Key del proveedor, restringida por dominio. Sin ella no hay mapa. */
  apiKey: string | undefined;
  language?: string;
}

/**
 * La identidad de LO QUE EL MAPA DESCRIBE. Los buscadores la usan de `key`
 * del control: cuando cambia, React monta uno nuevo —colapsado y sin el
 * iframe del anterior—, que es la única forma de que "cada búsqueda nueva
 * arranca cerrada" se cumpla de verdad (cerrarlo por estado dejaría el mapa
 * viejo montado, apenas escondido).
 *
 * No alcanza con la identidad del payload: los puntos resueltos que se
 * dibujan también pueden cambiar mientras la referencia contractual sigue
 * igual. Si esos puntos no entraran en la key, el mapa anterior podría quedar
 * abierto sobre otra resolución, cargando el proveedor sin que nadie lo
 * pidiera.
 *
 * Por eso la identidad se arma con los puntos DIBUJADOS —saneados por el
 * mismo camino que los dibuja—, y no con una lista de campos por vertical que
 * haya que acordarse de ampliar cada vez que aparezca otro desambiguador.
 */
export function searchRouteMapKey(
  searchIdentity: string | undefined,
  points?: RouteMapPoint[] | null,
): string {
  // Serializado, no concatenado: los textos del punto traen comas (una
  // dirección son puras comas), y pegados con un separador dos puntos
  // distintos podrían leerse igual y compartir control.
  const drawn = JSON.stringify(
    routeMapPoints(points).map((point) => [
      point.lat,
      point.lng,
      point.label ?? '',
      point.address ?? '',
      point.source ?? '',
    ]),
  );
  return `${searchIdentity ?? ''}#${drawn}`;
}

/**
 * ¿Hay mapa para esto, y con qué URL? ÚNICA definición de "se puede dibujar"
 * (21.9): la usan la card que lo dibuja (`RouteMapCard`) y el control que
 * ofrece abrirlo en la vista de resultados (`SearchRouteMap`). Si fueran dos
 * expresiones parecidas, el "Ver ruta" podría aparecer sobre una búsqueda que
 * al expandirse no muestra nada — un botón muerto.
 *
 * Sanea los puntos por el mismo camino que el resto: un valor inesperado del
 * backend o del formulario no puede terminar en una URL con coordenadas rotas.
 */
export function routeMapSrc({
  vertical,
  points,
  apiKey,
  language,
}: {
  vertical: string;
  points?: RouteMapPoint[] | null;
  apiKey: string | undefined;
  language?: string;
}): string | null {
  const usable = routeMapPoints(points);
  const mode = routeMapMode(vertical, usable);
  return mode ? routeMapEmbedUrl({ points: usable, mode, apiKey, language }) : null;
}

/**
 * La URL del iframe, o `null` si falta algo (key, puntos, modo). Nunca se
 * arma un mapa a medias: la card entera no se renderiza.
 */
export function routeMapEmbedUrl({
  points,
  mode,
  apiKey,
  language,
}: RouteMapEmbedInput): string | null {
  const key = apiKey?.trim();
  if (!key) return null;
  if (mode === 'single' ? points.length < 1 : points.length < 2) return null;

  const params = new URLSearchParams({ key });
  const lang = embedLanguage(language);
  if (lang) params.set('language', lang);

  if (mode === 'single') {
    params.set('q', coord(points[0]));
    return `${EMBED_BASE}/place?${params.toString()}`;
  }

  if (mode === 'centered') {
    params.set('center', coord(centerOf(points[0], points[1])));
    params.set('zoom', String(fitZoom(points[0], points[1])));
    return `${EMBED_BASE}/view?${params.toString()}`;
  }

  const byAddress = addressesTellEndpointsApart(points[0], points[1]);
  params.set('origin', directionsPoint(points[0], byAddress));
  params.set('destination', directionsPoint(points[1], byAddress));
  params.set('mode', PROVIDER_TRAVEL_MODE[mode]);
  return `${EMBED_BASE}/directions?${params.toString()}`;
}
